import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import api from "@/lib/api";
import Icon from "@/components/Icon";
import Logo from "@/components/Logo";
import { TextField, IconPicker, ColorField, BulletsField } from "@/pages/admin/Fields";
import ListEditor from "@/pages/admin/ListEditor";

const TABS = [
  { id: "home", label: "Home" },
  { id: "products", label: "Products" },
  { id: "solutions", label: "Solutions" },
  { id: "why_us", label: "Why Us" },
  { id: "contact", label: "Contact" },
  { id: "footer", label: "Footer" },
  { id: "submissions", label: "Submissions" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { content, reload } = useContent();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [activeTab, setActiveTab] = useState("home");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (content && !draft) setDraft(JSON.parse(JSON.stringify(content)));
  }, [content, draft]);

  if (!draft) return <div className="sp-container py-20 text-center text-slate-500">Loading editor…</div>;

  const setSection = (key, next) => setDraft({ ...draft, [key]: next });

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const payload = { ...draft };
      delete payload._id;
      delete payload.updated_at;
      delete payload.updated_by;
      const { data } = await api.put("/admin/content", payload);
      setDraft(JSON.parse(JSON.stringify(data)));
      await reload();
      setSavedAt(new Date());
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Reset ALL content to factory defaults? Your edits will be lost.")) return;
    setSaving(true); setError(null);
    try {
      const { data } = await api.post("/admin/content/reset");
      setDraft(JSON.parse(JSON.stringify(data)));
      await reload();
      setSavedAt(new Date());
    } catch (e) {
      setError("Reset failed");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => { logout(); navigate("/"); };

  return (
    <div data-testid="page-admin">
      {/* Admin top bar */}
      <header className="bg-navy text-white sticky top-0 z-40 shadow">
        <div className="sp-container py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <Link to="/"><Logo variant="dark" /></Link>
            <span className="hidden md:inline text-xs uppercase tracking-[0.18em] text-gold font-bold border-l border-white/20 pl-6">Admin Console</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-white/80">{user?.email}</span>
            {savedAt && (
              <span className="hidden sm:inline text-xs text-green-300" data-testid="admin-saved-at">
                Saved {savedAt.toLocaleTimeString()}
              </span>
            )}
            <button onClick={resetDefaults} className="text-xs text-white/70 hover:text-white px-2 py-1.5" data-testid="admin-reset" disabled={saving}>
              <Icon name="RotateCcw" size={14} className="inline mr-1" /> Reset
            </button>
            <button onClick={save} disabled={saving} className="sp-btn-gold disabled:opacity-60" data-testid="admin-save">
              <Icon name="Save" size={16} /> {saving ? "Saving…" : "Save changes"}
            </button>
            <button onClick={handleLogout} className="text-white/80 hover:text-white" aria-label="Log out" data-testid="admin-logout">
              <Icon name="LogOut" size={18} />
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="sp-container pb-3 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition ${
                activeTab === t.id ? "bg-gold text-navy" : "text-white/80 hover:text-white hover:bg-white/10"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      {error && (
        <div className="sp-container py-3">
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm" data-testid="admin-error">
            {error}
          </div>
        </div>
      )}

      <main className="sp-container py-10">
        {activeTab === "home" && <HomeEditor data={draft.home || {}} onChange={(v) => setSection("home", v)} />}
        {activeTab === "products" && <ProductsEditor data={draft.products || {}} onChange={(v) => setSection("products", v)} />}
        {activeTab === "solutions" && <SolutionsEditor data={draft.solutions || {}} onChange={(v) => setSection("solutions", v)} />}
        {activeTab === "why_us" && <WhyUsEditor data={draft.why_us || {}} onChange={(v) => setSection("why_us", v)} />}
        {activeTab === "contact" && <ContactEditor data={draft.contact || {}} onChange={(v) => setSection("contact", v)} />}
        {activeTab === "footer" && <FooterEditor data={draft.footer || {}} onChange={(v) => setSection("footer", v)} />}
        {activeTab === "submissions" && <SubmissionsPanel />}
      </main>
    </div>
  );
}

// ============================================================
// Per-page editors
// ============================================================

function Card({ title, children }) {
  return (
    <section className="sp-card mb-6" data-testid={`admin-card-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <h3 className="text-base font-extrabold text-navy uppercase tracking-wider mb-4">{title}</h3>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function HomeEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div>
        <Card title="Hero">
          <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} testid="home-eyebrow" />
          <TextField label="Headline (top line)" value={data.headline_top} onChange={(v) => set("headline_top", v)} testid="home-headline-top" />
          <TextField label="Headline (bottom line, gold)" value={data.headline_bottom} onChange={(v) => set("headline_bottom", v)} testid="home-headline-bottom" />
          <TextField label="Sub-headline" value={data.subheadline} onChange={(v) => set("subheadline", v)} multiline testid="home-subheadline" />
          <TextField label="Description" value={data.description} onChange={(v) => set("description", v)} multiline testid="home-description" />
          <TextField label="Primary CTA label" value={data.primary_cta} onChange={(v) => set("primary_cta", v)} testid="home-primary-cta" />
          <TextField label="Secondary CTA label" value={data.secondary_cta} onChange={(v) => set("secondary_cta", v)} testid="home-secondary-cta" />
          <TextField label="Hero image URL (Nigerian/African professional)" value={data.hero_image} onChange={(v) => set("hero_image", v)} testid="home-hero-image" />
        </Card>
        <Card title="Pipeline">
          <TextField label="Caption" value={data.pipeline_caption} onChange={(v) => set("pipeline_caption", v)} multiline testid="home-pipeline-caption" />
          <ListEditor
            label="Pipeline steps"
            items={data.pipeline}
            onChange={(v) => set("pipeline", v)}
            newItem={{ icon: "Lightbulb", label: "New stage" }}
            ItemEditor={({ item, onChange, testidBase }) => (
              <div className="grid sm:grid-cols-2 gap-3">
                <IconPicker value={item.icon} onChange={(v) => onChange({ ...item, icon: v })} testid={`${testidBase}-icon`} />
                <TextField label="Label" value={item.label} onChange={(v) => onChange({ ...item, label: v })} testid={`${testidBase}-label`} />
              </div>
            )}
            testidBase="home-pipeline"
          />
        </Card>
      </div>

      <div>
        <Card title="Services strip (6 items)">
          <ListEditor
            label="Services"
            items={data.services}
            onChange={(v) => set("services", v)}
            newItem={{ icon: "Target", label: "New Service", color: "#0a1f5c" }}
            ItemEditor={({ item, onChange, testidBase }) => (
              <div className="grid sm:grid-cols-3 gap-3">
                <IconPicker value={item.icon} onChange={(v) => onChange({ ...item, icon: v })} testid={`${testidBase}-icon`} />
                <TextField label="Label" value={item.label} onChange={(v) => onChange({ ...item, label: v })} testid={`${testidBase}-label`} />
                <ColorField value={item.color} onChange={(v) => onChange({ ...item, color: v })} testid={`${testidBase}-color`} />
              </div>
            )}
            testidBase="home-services"
          />
        </Card>
        <Card title="Value pillars">
          <ListEditor
            label="Values"
            items={data.values}
            onChange={(v) => set("values", v)}
            newItem={{ icon: "Target", title: "New Value", text: "Description here." }}
            ItemEditor={({ item, onChange, testidBase }) => (
              <div className="grid sm:grid-cols-3 gap-3">
                <IconPicker value={item.icon} onChange={(v) => onChange({ ...item, icon: v })} testid={`${testidBase}-icon`} />
                <TextField label="Title" value={item.title} onChange={(v) => onChange({ ...item, title: v })} testid={`${testidBase}-title`} />
                <TextField label="Text" value={item.text} onChange={(v) => onChange({ ...item, text: v })} multiline testid={`${testidBase}-text`} />
              </div>
            )}
            testidBase="home-values"
          />
        </Card>
      </div>
    </div>
  );
}

function ProductsEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <>
      <Card title="Page header">
        <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} testid="prod-eyebrow" />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Title" value={data.title} onChange={(v) => set("title", v)} testid="prod-title" />
          <TextField label="Title accent (gold)" value={data.title_accent} onChange={(v) => set("title_accent", v)} testid="prod-title-accent" />
        </div>
        <TextField label="Description" value={data.description} onChange={(v) => set("description", v)} multiline testid="prod-description" />
      </Card>
      <Card title="Categories">
        <ListEditor
          label="Product categories"
          items={data.categories}
          onChange={(v) => set("categories", v)}
          newItem={{ title: "New Category", color: "#0a1f5c", icon: "Building2", items: [] }}
          ItemEditor={({ item, onChange, testidBase }) => (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-3 gap-3">
                <TextField label="Category title" value={item.title} onChange={(v) => onChange({ ...item, title: v })} testid={`${testidBase}-title`} />
                <IconPicker value={item.icon} onChange={(v) => onChange({ ...item, icon: v })} testid={`${testidBase}-icon`} />
                <ColorField value={item.color} onChange={(v) => onChange({ ...item, color: v })} testid={`${testidBase}-color`} />
              </div>
              <ListEditor
                label="Products in category"
                items={item.items}
                onChange={(v) => onChange({ ...item, items: v })}
                newItem={{ icon: "Target", title: "New Product", text: "Short description." }}
                ItemEditor={({ item: p, onChange: pc, testidBase: pb }) => (
                  <div className="grid sm:grid-cols-3 gap-3">
                    <IconPicker value={p.icon} onChange={(v) => pc({ ...p, icon: v })} testid={`${pb}-icon`} />
                    <TextField label="Product title" value={p.title} onChange={(v) => pc({ ...p, title: v })} testid={`${pb}-title`} />
                    <TextField label="Description" value={p.text} onChange={(v) => pc({ ...p, text: v })} multiline testid={`${pb}-text`} />
                  </div>
                )}
                testidBase={`${testidBase}-items`}
              />
            </div>
          )}
          testidBase="prod-categories"
        />
      </Card>
    </>
  );
}

function SolutionsEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <>
      <Card title="Page header">
        <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} testid="sol-eyebrow" />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Title (gold)" value={data.title} onChange={(v) => set("title", v)} testid="sol-title" />
          <TextField label="Title accent (navy)" value={data.title_accent} onChange={(v) => set("title_accent", v)} testid="sol-title-accent" />
        </div>
        <TextField label="Description" value={data.description} onChange={(v) => set("description", v)} multiline testid="sol-description" />
      </Card>

      <Card title="Outcomes strip">
        <ListEditor
          label="Outcomes"
          items={data.outcomes}
          onChange={(v) => set("outcomes", v)}
          newItem={{ icon: "Target", label: "New Outcome" }}
          ItemEditor={({ item, onChange, testidBase }) => (
            <div className="grid sm:grid-cols-2 gap-3">
              <IconPicker value={item.icon} onChange={(v) => onChange({ ...item, icon: v })} testid={`${testidBase}-icon`} />
              <TextField label="Label" value={item.label} onChange={(v) => onChange({ ...item, label: v })} testid={`${testidBase}-label`} />
            </div>
          )}
          testidBase="sol-outcomes"
        />
      </Card>

      <Card title="Solution cards (problem → solution)">
        <ListEditor
          label="Solutions"
          items={data.items}
          onChange={(v) => set("items", v)}
          newItem={() => ({ n: (data.items?.length || 0) + 1, color: "#0a1f5c", title: "New Solution", problem: "Customer problem.", solution: "Our solution.", bullets: [] })}
          ItemEditor={({ item, onChange, testidBase }) => (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-3 gap-3">
                <TextField label="Title" value={item.title} onChange={(v) => onChange({ ...item, title: v })} testid={`${testidBase}-title`} />
                <ColorField value={item.color} onChange={(v) => onChange({ ...item, color: v })} testid={`${testidBase}-color`} />
                <TextField label="Number (for badge)" value={item.n} onChange={(v) => onChange({ ...item, n: Number(v) || 0 })} testid={`${testidBase}-n`} />
              </div>
              <TextField label="Problem" value={item.problem} onChange={(v) => onChange({ ...item, problem: v })} multiline testid={`${testidBase}-problem`} />
              <TextField label="Solution headline" value={item.solution} onChange={(v) => onChange({ ...item, solution: v })} testid={`${testidBase}-solution`} />
              <BulletsField value={item.bullets} onChange={(v) => onChange({ ...item, bullets: v })} testid={`${testidBase}-bullets`} />
            </div>
          )}
          testidBase="sol-items"
        />
      </Card>

      <Card title="Bottom pillars">
        <ListEditor
          label="Pillars"
          items={data.pillars}
          onChange={(v) => set("pillars", v)}
          newItem={{ icon: "Target", label: "New Pillar" }}
          ItemEditor={({ item, onChange, testidBase }) => (
            <div className="grid sm:grid-cols-2 gap-3">
              <IconPicker value={item.icon} onChange={(v) => onChange({ ...item, icon: v })} testid={`${testidBase}-icon`} />
              <TextField label="Label" value={item.label} onChange={(v) => onChange({ ...item, label: v })} testid={`${testidBase}-label`} />
            </div>
          )}
          testidBase="sol-pillars"
        />
      </Card>
    </>
  );
}

function WhyUsEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <>
      <Card title="Page header">
        <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} testid="why-eyebrow" />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Title" value={data.title} onChange={(v) => set("title", v)} testid="why-title" />
          <TextField label="Title accent (gold)" value={data.title_accent} onChange={(v) => set("title_accent", v)} testid="why-title-accent" />
        </div>
        <TextField label="Description" value={data.description} onChange={(v) => set("description", v)} multiline testid="why-description" />
        <TextField label="Team image URL" value={data.team_image} onChange={(v) => set("team_image", v)} testid="why-team-image" />
      </Card>

      <Card title="Why pillars">
        <ListEditor
          label="Pillars"
          items={data.pillars}
          onChange={(v) => set("pillars", v)}
          newItem={{ icon: "Target", title: "New Pillar", text: "Description." }}
          ItemEditor={({ item, onChange, testidBase }) => (
            <div className="grid sm:grid-cols-3 gap-3">
              <IconPicker value={item.icon} onChange={(v) => onChange({ ...item, icon: v })} testid={`${testidBase}-icon`} />
              <TextField label="Title" value={item.title} onChange={(v) => onChange({ ...item, title: v })} testid={`${testidBase}-title`} />
              <TextField label="Text" value={item.text} onChange={(v) => onChange({ ...item, text: v })} multiline testid={`${testidBase}-text`} />
            </div>
          )}
          testidBase="why-pillars"
        />
      </Card>

      <Card title="Featured projects">
        <ListEditor
          label="Projects"
          items={data.projects}
          onChange={(v) => set("projects", v)}
          newItem={{ icon: "Folder", color: "#0a1f5c", title: "New Project", text: "Short description." }}
          ItemEditor={({ item, onChange, testidBase }) => (
            <div className="grid sm:grid-cols-4 gap-3">
              <IconPicker value={item.icon} onChange={(v) => onChange({ ...item, icon: v })} testid={`${testidBase}-icon`} />
              <ColorField value={item.color} onChange={(v) => onChange({ ...item, color: v })} testid={`${testidBase}-color`} />
              <TextField label="Title" value={item.title} onChange={(v) => onChange({ ...item, title: v })} testid={`${testidBase}-title`} />
              <TextField label="Text" value={item.text} onChange={(v) => onChange({ ...item, text: v })} multiline testid={`${testidBase}-text`} />
            </div>
          )}
          testidBase="why-projects"
        />
      </Card>

      <Card title="Positioning block">
        <TextField label="Title" value={data.positioning_title} onChange={(v) => set("positioning_title", v)} testid="why-pos-title" />
        <TextField label="Text" value={data.positioning_text} onChange={(v) => set("positioning_text", v)} multiline testid="why-pos-text" />
      </Card>
    </>
  );
}

function ContactEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const topics = Array.isArray(data.topics) ? data.topics : [];
  return (
    <>
      <Card title="Page header">
        <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} testid="contact-eyebrow" />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Title" value={data.title} onChange={(v) => set("title", v)} testid="contact-title" />
          <TextField label="Title accent (gold)" value={data.title_accent} onChange={(v) => set("title_accent", v)} testid="contact-title-accent" />
        </div>
        <TextField label="Description" value={data.description} onChange={(v) => set("description", v)} multiline testid="contact-description" />
        <TextField label="Team image URL" value={data.team_image} onChange={(v) => set("team_image", v)} testid="contact-team-image" />
      </Card>

      <Card title="Contact details">
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Phone (display)" value={data.phone} onChange={(v) => set("phone", v)} testid="contact-phone" />
          <TextField label="Phone (dial / no spaces)" value={data.phone_dial} onChange={(v) => set("phone_dial", v)} testid="contact-phone-dial" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Email" value={data.email} onChange={(v) => set("email", v)} testid="contact-email-addr" />
          <TextField label="WhatsApp URL" value={data.whatsapp_url} onChange={(v) => set("whatsapp_url", v)} testid="contact-whatsapp" />
        </div>
        <TextField label="Audience tagline (e.g. Startups · Businesses · Governments)" value={data.audience} onChange={(v) => set("audience", v)} testid="contact-audience" />
      </Card>

      <Card title="Form">
        <TextField label="Form title" value={data.form_title} onChange={(v) => set("form_title", v)} testid="contact-form-title" />
        <TextField label="Form subtitle" value={data.form_subtitle} onChange={(v) => set("form_subtitle", v)} multiline testid="contact-form-subtitle" />
        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Topics (one per line)</label>
          <textarea
            value={topics.join("\n")}
            onChange={(e) => set("topics", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
            rows={5}
            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-navy font-mono"
            data-testid="contact-topics"
          />
        </div>
      </Card>

      <Card title="Reasons to work with us">
        <ListEditor
          label="Reasons"
          items={data.reasons}
          onChange={(v) => set("reasons", v)}
          newItem={{ icon: "Target", title: "New Reason", text: "Why this matters." }}
          ItemEditor={({ item, onChange, testidBase }) => (
            <div className="grid sm:grid-cols-3 gap-3">
              <IconPicker value={item.icon} onChange={(v) => onChange({ ...item, icon: v })} testid={`${testidBase}-icon`} />
              <TextField label="Title" value={item.title} onChange={(v) => onChange({ ...item, title: v })} testid={`${testidBase}-title`} />
              <TextField label="Text" value={item.text} onChange={(v) => onChange({ ...item, text: v })} multiline testid={`${testidBase}-text`} />
            </div>
          )}
          testidBase="contact-reasons"
        />
      </Card>

      <Card title="Closing">
        <TextField label="Closing quote" value={data.closing_quote} onChange={(v) => set("closing_quote", v)} multiline testid="contact-closing-quote" />
      </Card>
    </>
  );
}

function FooterEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <Card title="Footer">
      <TextField label="Tagline" value={data.tagline} onChange={(v) => set("tagline", v)} multiline testid="footer-tagline" />
      <div className="grid sm:grid-cols-2 gap-3">
        <TextField label="Phone (display)" value={data.phone} onChange={(v) => set("phone", v)} testid="footer-phone" />
        <TextField label="Phone (dial)" value={data.phone_dial} onChange={(v) => set("phone_dial", v)} testid="footer-phone-dial" />
      </div>
      <TextField label="Email" value={data.email} onChange={(v) => set("email", v)} testid="footer-email" />
      <TextField label="Location" value={data.location} onChange={(v) => set("location", v)} testid="footer-location" />
      <TextField label="Footer quote" value={data.footer_quote} onChange={(v) => set("footer_quote", v)} multiline testid="footer-quote" />
    </Card>
  );
}

function SubmissionsPanel() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/contact");
      setRows(data);
    } catch (e) { setError("Failed to load submissions"); }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await api.delete(`/admin/contact/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
    } catch { setError("Delete failed"); }
  };

  if (error) return <div className="text-red-600">{error}</div>;
  if (!rows) return <div className="text-slate-500">Loading…</div>;

  return (
    <div data-testid="admin-submissions">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-extrabold text-navy">Contact submissions <span className="text-slate-400 font-normal">({rows.length})</span></h2>
        <button onClick={load} className="text-xs font-semibold text-navy hover:text-gold-deep px-3 py-1.5 rounded-lg border border-slate-200" data-testid="admin-submissions-refresh">
          Refresh
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="text-center text-slate-500 py-12 border border-dashed border-slate-200 rounded-2xl">
          No submissions yet. They'll appear here as soon as someone fills out the contact form.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="sp-card" data-testid={`submission-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-navy font-extrabold">{r.name} <span className="text-slate-400 font-normal text-sm">· {r.email}</span></div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {new Date(r.created_at).toLocaleString()} · {r.topic || "General Enquiry"}
                    {r.phone && <> · {r.phone}</>}
                    {r.company && <> · {r.company}</>}
                  </div>
                  <p className="text-sm text-slate-700 mt-3 whitespace-pre-wrap">{r.message}</p>
                </div>
                <button onClick={() => del(r.id)} className="text-red-500 hover:text-red-700 text-xs font-semibold" data-testid={`submission-delete-${r.id}`}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
